<template>
  <div class="pagination">
    <div class="pagination__info">
      Страница {{ currentPage }} из {{ pageCount }}
    </div>

    <div class="pagination__pages">
      <VButton
        icon="arrow-pagination"
        block="pagination"
        size="init"
        class="prev-page"
        :disabled="disabled || isFirstPage"
        @click.native="setPage(currentPage - 1)"
      />

      <button
        :class="{ active: 1 === currentPage }"
        @click.stop="setPage(1)"
        :disabled="disabled"
      >
        1
      </button>

      <button
        v-if="showLeftDots"
        class="pagination__dots"
        :disabled="disabled"
        @click.stop="togglePopup('left', $event)"
      >
        ...
      </button>

      <button
        v-for="page in pagination"
        :key="page"
        :class="{ active: page === currentPage }"
        @click="setPage(page)"
        :disabled="disabled"
      >
        {{ page }}
      </button>

      <button
        v-if="showRightDots"
        class="pagination__dots"
        :disabled="disabled"
        @click.stop="togglePopup('right', $event)"
      >
        ...
      </button>

      <button
        v-if="pageCount !== 1"
        :disabled="disabled"
        :class="{ active: pageCount === currentPage }"
        @click.stop="setPage(pageCount)"
      >
        {{ pageCount }}
      </button>

      <VButton
        :icon="{ name: 'arrow-pagination', dir: '180' }"
        block="pagination"
        size="init"
        class="last-page"
        :disabled="disabled || isLastPage"
        @click.native="setPage(currentPage + 1)"
      />

      <!-- Один общий popup -->
      <div
        v-if="openedPopup"
        ref="popup"
        class="pagination__popup"
        :style="popupStyle"
        @click.stop
      >
        <VInput
          v-model="pageSearch"
          :pattern="pattern.num"
        >
          <Validation for="pattern" key-text="num"/>
        </VInput>

        <div class="pagination__popup-list">
          <button
            v-for="page in popupPages"
            :key="page"
            :class="{ active: page === currentPage }"
            @click="selectPopupPage(page)"
          >
            {{ page }}
          </button>

          <div
            v-if="!popupPages.length"
            class="pagination__empty"
          >
            Не найден
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VButton from '@/components/simple/button/VButton'

import { createPageList } from '@/utils/store/pagination'
import VInput from '@/components/Form/Vinput/VInput.vue'
import Validation from '@/components/Form/Validation.vue'
import { PATTERN_LIST } from '@/const/validation'

export default Vue.extend({
  name: 'AppPagination',
  components: { Validation, VInput, VButton },
  props: {
    currentPage: Number,
    pageCount: Number,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      openedPopup: null,
      pageSearch: '',
      pattern: PATTERN_LIST,
      popupTarget: null
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  computed: {
    isFirstPage () {
      return this.currentPage === 1
    },

    isLastPage () {
      return this.currentPage === this.pageCount
    },

    pagination () {
      if (this.currentPage < 5) {
        return this.pageList.filter(page => page > 1 && page < 5 && page !== this.pageCount)
      }

      if (this.currentPage > this.pageCount - 3) {
        return this.pageList.filter(page => page > this.pageCount - 4 && page !== this.pageCount)
      }

      return this.pageList.filter(page => page >= this.currentPage - 1 && page <= this.currentPage + 1)
    },

    pageList () {
      return createPageList(this.pageCount)
    },

    showLeftDots () {
      const firstVisiblePage = this.pagination[0]

      return firstVisiblePage > 2
    },

    showRightDots () {
      const lastVisiblePage = this.pagination[this.pagination.length - 1]

      return lastVisiblePage < this.pageCount - 1
    },

    /**
     * Диапазон страниц, который сейчас должен
     * отображаться в popup.
     */
    popupRange () {
      if (!this.openedPopup) {
        return null
      }

      const visiblePages = this.pagination

      if (!visiblePages.length) {
        return null
      }

      if (this.openedPopup === 'left') {
        return {
          from: 2,
          to: visiblePages[0] - 1
        }
      }

      return {
        from: visiblePages[visiblePages.length - 1] + 1,
        to: this.pageCount - 1
      }
    },

    popupPages () {
      const search = this.pageSearch.trim()

      return this.pageList.filter(page => {
        const inRange =
          page >= this.popupRange.from &&
          page <= this.popupRange.to

        if (!inRange) {
          return false
        }

        if (!search) {
          return true
        }

        return String(page).includes(search)
      })
    },
    popupStyle () {
      if (!this.popupTarget) {
        return {}
      }

      return {
        left: `${this.popupTarget.offsetLeft + this.popupTarget.offsetWidth / 2}px`
      }
    }
  },
  methods: {
    togglePopup (side, event) {
      if (this.disabled) {
        return
      }

      if (this.openedPopup === side) {
        this.closePopup()
        return
      }

      this.openedPopup = side
      this.popupTarget = event.currentTarget
      this.pageSearch = ''
    },

    closePopup () {
      this.openedPopup = null
      this.pageSearch = ''
    },

    selectPopupPage (page) {
      this.setPage(page)
      this.closePopup()
    },
    setPage (pageNumber) {
      this.$emit('setPage', pageNumber)
    },
    handleOutsideClick (event) {
      const popup = this.$refs.popup

      if (popup && !popup.contains(event.target)) {
        this.closePopup()
      }
    }
  }
})
</script>

<style scoped lang="scss">
.pagination  {
  display: flex;
  padding: 30px 0;
  gap: 40px;

  &__pages {
    position: relative;
    display: flex;
  }

  &__popup {
    position: absolute;
    z-index: 10;
    bottom: calc(100% + 8px);
    left: 50%;
    display: flex;
    flex-direction: column;
    gap: var(--xxs);
    width: 71px;
    max-height: 160px;
    padding: var(--xxs);
    background: #fff;
    border: 1px solid #DBE3F2;
    border-radius: 11px;
    transform: translateX(-50%);

    &-list {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      overflow-x: hidden;

      &::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      &::-webkit-scrollbar-track {
        background: $color__scrollbar--bg;
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background: $color__main;
        border-radius: 10px;
      }

      button {
        text-align: center;
        padding: 0;
        transition: $transition;

        &:hover {
          color: $color__main;
        }
      }
    }
  }

  &__search {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 8px;
    padding: 8px 10px;
  }

  &__empty {
    padding: 10px;
    text-align: center;
  }

  @media screen and (max-width: $tablet-width-s) {
    &__pages {
      & button {
        padding: 0 10px;
      }
      & .prev-page {
        padding-left: 0;
      }

      & .next-page {
        padding-right: 0;
      }
    }

  }

  @media screen and (max-width: 400px) {
    flex-direction: column;
    gap: 10px;
  }
}

.active {
  color: $color__main;
  pointer-events: none;
}
</style>
